import { useEffect, useMemo, useState } from "react";
// Ajout pour modale politique/annulation
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AnimatedScroll from "../../components/AnimatedScroll/AnimatedScroll";
import DishGalleryCarousel from "../../components/DishGalleryCarousel/DishGalleryCarousel";
import {
  fetchDishGalleryFromApi,
} from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";
import {
  createReservationDepositIntent,
  createReservation,
  fetchReservationAvailabilityPublic,
  fetchReservationDepositPolicy,
  fetchReservationPublicSettings,
} from "../../utils/reservationApi";
import "./reservation.css";

const stripePublicKey = String(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || "",
).trim();
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

const DEFAULT_RESERVATION_RULES = {
  tableServices: [
    {
      id: "midi",
      label: "Midi",
      start: "11:00",
      end: "13:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
    {
      id: "soir",
      label: "Soir",
      start: "18:00",
      end: "20:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
  ],
};

function getTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLocalIsoDate(dateValue) {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(dateKey) {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateKey;
  }

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatMonthLabel(dateValue) {
  return dateValue.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function formatDepositAmount(cents, currency = "eur") {
  const amount = Math.max(0, Number(cents || 0)) / 100;
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: String(currency || "eur").toUpperCase(),
    }).format(amount);
  } catch (_error) {
    return `${amount.toFixed(2)} EUR`;
  }
}

function startOfMonth(dateValue) {
  return new Date(dateValue.getFullYear(), dateValue.getMonth(), 1);
}

function addMonths(dateValue, amount) {
  return new Date(dateValue.getFullYear(), dateValue.getMonth() + amount, 1);
}

function timeToMinutes(value) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value || ""));
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour * 60 + minute;
}

function isSlotInFutureForDate(dateKey, timeValue, nowDate) {
  if (!dateKey || !timeValue) {
    return false;
  }

  const todayKey = getLocalIsoDate(nowDate);
  if (dateKey > todayKey) {
    return true;
  }

  if (dateKey < todayKey) {
    return false;
  }

  const slotMinutes = timeToMinutes(timeValue);
  if (slotMinutes === null) {
    return false;
  }

  const nowMinutes = nowDate.getHours() * 60 + nowDate.getMinutes();
  return slotMinutes > nowMinutes;
}

function getFirstServiceId(rules) {
  return Array.isArray(rules?.tableServices) && rules.tableServices.length > 0
    ? rules.tableServices[0].id
    : "";
}

function buildInitialState(rules) {
  return {
    reservationType: "table",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    requestedDate: "",
    tableServiceId: getFirstServiceId(rules),
    requestedTime: "",
    guestCount: "2",
    message: "",
    website: "",
    cbFirstName: "",
    cbLastName: "",
    cbAddress: "",
    cbPostal: "",
  };
}

function ReservationFormContent({
  stripeUnavailable = false,
  unavailableMessage = null,
  blocked = false,
  forcedType = null,
  heading = "Reserver une table",
  manageDocumentTitle = true,
  stripe = null,
  elements = null,
}) {
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  if (manageDocumentTitle) {
    document.title = "Carpe Diem - Réservation Restaurant";
  }

  const [reservationRules, setReservationRules] = useState(
    DEFAULT_RESERVATION_RULES,
  );
  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [dishGallery, setDishGallery] = useState([]);
  const [formData, setFormData] = useState(() =>
    buildInitialState(DEFAULT_RESERVATION_RULES),
  );
  const [calendarMonth, setCalendarMonth] = useState(() =>
    startOfMonth(new Date()),
  );
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [depositPolicy, setDepositPolicy] = useState({
    enabled: false,
    testMode: false,
    currency: "eur",
    tablePerGuestCents: 0,
    roomPerNightCents: 0,
  });
  const [depositPolicyLoading, setDepositPolicyLoading] = useState(true);
  const [acceptDepositPolicy, setAcceptDepositPolicy] = useState(false);
  const hasForcedType = forcedType === "table";

  const todayIso = useMemo(() => getTodayIsoDate(), []);
  const nowDate = useMemo(() => new Date(nowTick), [nowTick]);
  const todayLiveIso = useMemo(() => getLocalIsoDate(nowDate), [nowDate]);
  const weekDayLabels = useMemo(
    () => ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
    [],
  );
  const shouldRequireDeposit = depositPolicy.enabled;
  const depositPreviewCents = useMemo(() => {
    if (!shouldRequireDeposit) {
      return 0;
    }

    const guests = Math.max(1, Number.parseInt(formData.guestCount, 10) || 1);
    return guests * Number(depositPolicy.tablePerGuestCents || 0);
  }, [
    shouldRequireDeposit,
    depositPolicy.tablePerGuestCents,
    formData.guestCount,
  ]);

  const services = useMemo(
    () =>
      Array.isArray(reservationRules?.tableServices)
        ? reservationRules.tableServices
        : [],
    [reservationRules],
  );

  const liveAvailabilityRows = useMemo(
    () =>
      availabilityRows.map((row) => {
        const dateKey = String(row?.date || "");
        const serviceRows = Array.isArray(row?.services)
          ? row.services.map((serviceItem) => {
              const availableTimes = Array.isArray(serviceItem?.availableTimes)
                ? serviceItem.availableTimes.filter((timeValue) =>
                    isSlotInFutureForDate(dateKey, timeValue, nowDate),
                  )
                : [];

              return {
                ...serviceItem,
                availableTimes,
                isFull: availableTimes.length === 0,
              };
            })
          : [];

        const isDateFull =
          serviceRows.length > 0 &&
          serviceRows.every(
            (serviceItem) => serviceItem.availableTimes.length === 0,
          );

        return {
          ...row,
          date: dateKey,
          services: serviceRows,
          isDateFull,
        };
      }),
    [availabilityRows, nowDate],
  );

  const availableDateRows = useMemo(
    () =>
      liveAvailabilityRows.filter((row) => {
        if (!row?.date || row.date < todayLiveIso) {
          return false;
        }

        const serviceRows = Array.isArray(row.services) ? row.services : [];
        const selectedServiceRow = serviceRows.find(
          (serviceItem) => serviceItem.serviceId === formData.tableServiceId,
        );

        return (
          Boolean(selectedServiceRow) &&
          Array.isArray(selectedServiceRow.availableTimes) &&
          selectedServiceRow.availableTimes.length > 0
        );
      }),
    [formData.tableServiceId, liveAvailabilityRows, todayLiveIso],
  );

  const availableDateSet = useMemo(
    () => new Set(availableDateRows.map((row) => row.date)),
    [availableDateRows],
  );

  const selectedDateAvailability = useMemo(
    () =>
      liveAvailabilityRows.find(
        (row) => row?.date === formData.requestedDate,
      ) || null,
    [formData.requestedDate, liveAvailabilityRows],
  );

  const selectedServiceAvailability = useMemo(() => {
    if (!selectedDateAvailability || !formData.tableServiceId) {
      return null;
    }

    const rows = Array.isArray(selectedDateAvailability.services)
      ? selectedDateAvailability.services
      : [];
    return (
      rows.find(
        (serviceItem) => serviceItem.serviceId === formData.tableServiceId,
      ) || null
    );
  }, [selectedDateAvailability, formData.tableServiceId]);

  const availableTimes = useMemo(() => {
    if (!selectedServiceAvailability) {
      return [];
    }

    return Array.isArray(selectedServiceAvailability.availableTimes)
      ? selectedServiceAvailability.availableTimes
      : [];
  }, [selectedServiceAvailability]);

  const calendarDays = useMemo(() => {
    const firstDay = startOfMonth(calendarMonth);
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - firstDay.getDay());

    const days = [];
    for (let index = 0; index < 42; index += 1) {
      const value = new Date(gridStart);
      value.setDate(gridStart.getDate() + index);

      const dateKey = getLocalIsoDate(value);
      days.push({
        dateKey,
        dayOfMonth: value.getDate(),
        inCurrentMonth:
          value.getMonth() === calendarMonth.getMonth() &&
          value.getFullYear() === calendarMonth.getFullYear(),
        isAvailable: availableDateSet.has(dateKey),
        isSelected: formData.requestedDate === dateKey,
      });
    }

    return days;
  }, [availableDateSet, calendarMonth, formData.requestedDate]);

  const canGoPreviousMonth = useMemo(() => {
    const previous = addMonths(calendarMonth, -1);
    const endOfPrevious = new Date(
      previous.getFullYear(),
      previous.getMonth() + 1,
      0,
      23,
      59,
      59,
    );

    return getLocalIsoDate(endOfPrevious) >= todayLiveIso;
  }, [calendarMonth, todayLiveIso]);

  const hasAvailableInMonth = useMemo(
    () =>
      availableDateRows.some((row) => {
        const date = new Date(`${row.date}T00:00:00`);
        return (
          !Number.isNaN(date.getTime()) &&
          date.getMonth() === calendarMonth.getMonth() &&
          date.getFullYear() === calendarMonth.getFullYear()
        );
      }),
    [availableDateRows, calendarMonth],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const rules = await fetchReservationPublicSettings();
        if (!isMounted) {
          return;
        }

        setReservationRules(rules);
        setFormData((prev) => ({
          ...prev,
          tableServiceId: prev.tableServiceId || getFirstServiceId(rules),
        }));
      } catch (_error) {
        if (!isMounted) {
          return;
        }
        setReservationRules(DEFAULT_RESERVATION_RULES);
      } finally {
        if (isMounted) {
          setSettingsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const applyGallery = (galleryItems) => {
      if (!isMounted) {
        return;
      }

      const nextGallery = Array.isArray(galleryItems) ? galleryItems : [];
      setDishGallery(nextGallery);
      localStorage.setItem("carpeDiemDishGallery", JSON.stringify(nextGallery));
    };

    const loadGalleryFromCache = () => {
      const savedGallery = localStorage.getItem("carpeDiemDishGallery");
      if (!savedGallery) {
        setDishGallery([]);
        return;
      }

      const parsed = safeJsonParse(savedGallery, []);
      applyGallery(Array.isArray(parsed) ? parsed : []);
    };

    const syncGalleryFromApi = async () => {
      try {
        const galleryItems = await fetchDishGalleryFromApi();
        applyGallery(galleryItems);
      } catch (_error) {
        // Keep cache if the API is temporarily unavailable.
      }
    };

    loadGalleryFromCache();
    syncGalleryFromApi();

    const handleStorageChange = (event) => {
      if (event.key === "carpeDiemDishGallery") {
        loadGalleryFromCache();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("carpe-content-changed", syncGalleryFromApi);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncGalleryFromApi();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("carpe-content-changed", syncGalleryFromApi);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAvailability = async () => {
      setAvailabilityLoading(true);
      try {
        const rows = await fetchReservationAvailabilityPublic({
          from: todayIso,
          days: 60,
        });
        if (!isMounted) {
          return;
        }
        setAvailabilityRows(rows);
      } catch (_error) {
        if (!isMounted) {
          return;
        }
        setAvailabilityRows([]);
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    loadAvailability();

    return () => {
      isMounted = false;
    };
  }, [todayIso]);

  useEffect(() => {
    let isMounted = true;

    const loadDepositPolicy = async () => {
      setDepositPolicyLoading(true);
      try {
        const policy = await fetchReservationDepositPolicy();
        if (!isMounted) {
          return;
        }

        setDepositPolicy({
          enabled: Boolean(policy?.enabled),
          testMode: Boolean(policy?.testMode),
          currency: String(policy?.currency || "eur"),
          tablePerGuestCents: Number(policy?.tablePerGuestCents || 0),
          roomPerNightCents: Number(policy?.roomPerNightCents || 0),
        });
      } catch (_error) {
        if (!isMounted) {
          return;
        }
        setDepositPolicy({
          enabled: false,
          testMode: false,
          currency: "eur",
          tablePerGuestCents: 0,
          roomPerNightCents: 0,
        });
      } finally {
        if (isMounted) {
          setDepositPolicyLoading(false);
        }
      }
    };

    loadDepositPolicy();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      formData.requestedDate &&
      !availableDateSet.has(formData.requestedDate)
    ) {
      setFormData((prev) => ({
        ...prev,
        requestedDate: "",
        requestedTime: "",
      }));
      return;
    }

    if (
      formData.requestedTime &&
      !availableTimes.includes(formData.requestedTime)
    ) {
      setFormData((prev) => ({
        ...prev,
        requestedTime: "",
      }));
    }
  }, [
    availableDateSet,
    availableTimes,
    formData.requestedDate,
    formData.requestedTime,
  ]);

  useEffect(() => {
    if (!formData.requestedDate) {
      return;
    }

    const date = new Date(`${formData.requestedDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return;
    }

    const targetMonth = startOfMonth(date);
    if (
      targetMonth.getFullYear() !== calendarMonth.getFullYear() ||
      targetMonth.getMonth() !== calendarMonth.getMonth()
    ) {
      setCalendarMonth(targetMonth);
    }
  }, [calendarMonth, formData.requestedDate]);

  useEffect(() => {
    if (!hasForcedType || formData.reservationType === forcedType) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      reservationType: forcedType,
      requestedTime: prev.requestedTime,
      tableServiceId: prev.tableServiceId || getFirstServiceId(reservationRules),
    }));
  }, [
    forcedType,
    formData.reservationType,
    hasForcedType,
    reservationRules,
  ]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleServiceSelect = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      tableServiceId: serviceId,
      requestedDate: "",
      requestedTime: "",
    }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleDateSelect = (dateKey) => {
    setFormData((prev) => ({
      ...prev,
      requestedDate: dateKey,
      requestedTime: "",
    }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleTimeSelect = (timeValue) => {
    setFormData((prev) => ({
      ...prev,
      requestedTime: timeValue,
    }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const validate = () => {
    // Champs CB obligatoires uniquement quand la caution est active sur table
    if (shouldRequireDeposit) {
      if (String(formData.cbFirstName || "").trim().length < 2) {
        return "Veuillez renseigner le prénom figurant sur la carte bancaire.";
      }
      if (String(formData.cbLastName || "").trim().length < 2) {
        return "Veuillez renseigner le nom figurant sur la carte bancaire.";
      }
      if (String(formData.cbAddress || "").trim().length < 5) {
        return "Veuillez renseigner l'adresse de facturation complète.";
      }
      if (String(formData.cbPostal || "").trim().length < 3) {
        return "Veuillez renseigner un code postal valide pour la carte bancaire.";
      }
    }
    if (String(formData.firstName || "").trim().length < 2) {
      return "Veuillez renseigner votre prenom.";
    }

    if (String(formData.lastName || "").trim().length < 2) {
      return "Veuillez renseigner votre nom.";
    }

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formData.email || "").trim())
    ) {
      return "Veuillez saisir une adresse email valide.";
    }

    if (!/^[0-9+()\-\s.]{6,40}$/.test(String(formData.phone || "").trim())) {
      return "Veuillez saisir un numero de telephone valide.";
    }

    if (!formData.requestedDate) {
      return "Veuillez choisir une date.";
    }

    if (!formData.tableServiceId) {
      return "Veuillez choisir un service.";
    }

    if (!availableDateSet.has(formData.requestedDate)) {
      return "Cette date n'est plus disponible pour ce service.";
    }

    if (
      !/^([01]\d|2[0-3]):[0-5]\d$/.test(String(formData.requestedTime || ""))
    ) {
      return "Veuillez choisir un horaire.";
    }

    if (!availableTimes.includes(formData.requestedTime)) {
      return "Ce creneau n'est plus disponible.";
    }

    const guests = Number.parseInt(formData.guestCount, 10);
    if (!Number.isInteger(guests) || guests < 1 || guests > 20) {
      return "Le nombre de personnes doit etre compris entre 1 et 20.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        reservationType: "table",
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        requestedDate: formData.requestedDate,
        requestedTime: formData.requestedTime,
        tableServiceId: formData.tableServiceId,
        guestCount: Number.parseInt(formData.guestCount, 10),
        nightCount: 1,
        roomId: "",
        optionExtraBed: false,
        optionPet: false,
        optionBreakfast: false,
        message: String(formData.message || "").trim(),
        website: formData.website,
        cbFirstName: formData.cbFirstName,
        cbLastName: formData.cbLastName,
        cbAddress: formData.cbAddress,
        cbPostal: formData.cbPostal,
      };

      if (shouldRequireDeposit) {
        if (!acceptDepositPolicy) {
          setErrorMessage(
            "Vous devez accepter la politique de caution pour finaliser la reservation.",
          );
          setLoading(false);
          return;
        }

        if (stripeUnavailable) {
          setErrorMessage(
            "Le paiement de caution n'est pas disponible pour le moment. Merci de nous contacter.",
          );
          setLoading(false);
          return;
        }

        if (!stripe || !elements) {
          setErrorMessage("Le module de paiement est en cours de chargement.");
          setLoading(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setErrorMessage("Impossible d'initialiser le champ carte.");
          setLoading(false);
          return;
        }

        const intent = await createReservationDepositIntent(payload);
        if (intent?.enabled && intent?.required) {
          const confirmation = await stripe.confirmCardPayment(
            intent.clientSecret,
            {
              payment_method: {
                card: cardElement,
                billing_details: {
                  name:
                    `${String(formData.firstName || "").trim()} ${String(
                      formData.lastName || "",
                    ).trim()}`.trim() || undefined,
                  email: String(formData.email || "").trim() || undefined,
                  phone: String(formData.phone || "").trim() || undefined,
                },
              },
            },
          );

          if (confirmation?.error) {
            throw new Error(
              confirmation.error.message ||
                  "Paiement de caution refuse. Merci de verifier votre carte.",
            );
          }

          const intentStatus = String(
            confirmation?.paymentIntent?.status || "",
          );
            const isValidStatus = intentStatus === "requires_capture";
          if (!isValidStatus) {
            throw new Error(
                "La caution n'a pas pu etre validee. Merci de reessayer.",
            );
          }

          payload.depositPaymentIntentId =
            confirmation?.paymentIntent?.id || intent.paymentIntentId;
        }
      }

      await createReservation(payload);

      setSuccessMessage(
        "Votre demande a bien ete envoyee. Nous vous recontactons rapidement.",
      );
      setFormData(() => {
        const initialState = buildInitialState(reservationRules);
        if (hasForcedType) {
          initialState.reservationType = forcedType;
        }
        return initialState;
      });
      setAcceptDepositPolicy(false);

      if (elements) {
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          cardElement.clear();
        }
      }
    } catch (error) {
      setErrorMessage(
        error?.payload?.error ||
          error?.message ||
          "Le serveur est indisponible. Merci de reessayer.",
      );
    } finally {
      setLoading(false);
    }
  };

  const reservationPageClassName =
    "reservation-page reservation-align-top";
  const isFormLocked = blocked || (shouldRequireDeposit && stripeUnavailable);
  const lockedMessage =
    unavailableMessage ||
    "Les réservations en ligne sont temporairement indisponibles. Contactez-nous par téléphone ou par e-mail.";
  return (
    <div className={reservationPageClassName}>
      {dishGallery.length > 0 && (
        <DishGalleryCarousel items={dishGallery} />
      )}

      <AnimatedScroll>
        <h1>{heading}</h1>
        <p className="reservation-urgent">
          Besoin d'une reservation urgente ? Appelez-nous au
          <a
            className="linkScale reservation-call-link"
            href="tel:+33563714445"
          >
            +33 5 63 71 44 45
          </a>
          .
        </p>
        <p className="reservation-required-note">* Champs obligatoires</p>
      </AnimatedScroll>

      <div className="single-contact-box">
        <div className="contact-form reservation-form-panel">
          <form
            className={`reservation-form${isFormLocked ? " reservation-form-disabled" : ""}`}
            onSubmit={handleSubmit}
            noValidate
          >
            {isFormLocked && (
              <p className="reservation-inline-note reservation-inline-note-warning">
                {lockedMessage}
              </p>
            )}
            <fieldset
              className={`reservation-form-fieldset${isFormLocked ? " is-locked" : ""}`}
              disabled={isFormLocked}
            >
            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="firstName">Prenom *</label>
                  <input
                    id="firstName"
                    className="form-control"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={80}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="lastName">Nom *</label>
                  <input
                    id="lastName"
                    className="form-control"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={80}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    className="form-control"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    maxLength={180}
                    required
                  />
                </div>
              </div>

              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="phone">Telephone *</label>
                  <input
                    id="phone"
                    className="form-control"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={40}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="reservation-table-booking">
                <div className="reservation-step-block">
                  <p className="reservation-step-title">
                    1. Choisissez votre service
                  </p>
                  <div className="reservation-service-grid">
                    {services.map((serviceItem) => (
                      <button
                        key={serviceItem.id}
                        type="button"
                        className={`reservation-service-chip${
                          formData.tableServiceId === serviceItem.id
                            ? " active"
                            : ""
                        }`}
                        onClick={() => handleServiceSelect(serviceItem.id)}
                      >
                        <span>{serviceItem.label}</span>
                        <small>
                          {serviceItem.start} - {serviceItem.end}
                        </small>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="reservation-step-block">
                  <div className="reservation-calendar-header">
                    <p className="reservation-step-title">
                      2. Choisissez une date
                    </p>
                    <div className="reservation-calendar-nav">
                      <button
                        type="button"
                        className="reservation-calendar-arrow"
                        onClick={() =>
                          canGoPreviousMonth &&
                          setCalendarMonth((prev) => addMonths(prev, -1))
                        }
                        disabled={!canGoPreviousMonth}
                      >
                        {"<"}
                      </button>
                      <span>{formatMonthLabel(calendarMonth)}</span>
                      <button
                        type="button"
                        className="reservation-calendar-arrow"
                        onClick={() =>
                          setCalendarMonth((prev) => addMonths(prev, 1))
                        }
                      >
                        {">"}
                      </button>
                    </div>
                  </div>

                  <div className="reservation-calendar-grid reservation-calendar-weekdays">
                    {weekDayLabels.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>

                  <div className="reservation-calendar-grid reservation-calendar-days">
                    {calendarDays.map((day) => (
                      <button
                        key={day.dateKey}
                        type="button"
                        className={`reservation-day-cell${
                          day.inCurrentMonth ? "" : " outside"
                        }${day.isSelected ? " selected" : ""}`}
                        onClick={() =>
                          day.isAvailable && handleDateSelect(day.dateKey)
                        }
                        disabled={!day.isAvailable}
                      >
                        {day.dayOfMonth}
                      </button>
                    ))}
                  </div>

                  {!hasAvailableInMonth && !availabilityLoading && (
                    <p className="reservation-inline-note reservation-inline-note-warning">
                      Aucun creneau disponible sur ce mois pour ce service.
                    </p>
                  )}
                </div>

                <div className="reservation-step-block">
                  <p className="reservation-step-title">
                    3. Choisissez une heure
                  </p>
                  {!formData.requestedDate && (
                    <p className="reservation-inline-note">
                      Selectionnez d'abord une date pour voir les horaires
                      disponibles.
                    </p>
                  )}

                  {formData.requestedDate && availableTimes.length === 0 && (
                    <p className="reservation-inline-note reservation-inline-note-warning">
                      Plus de creneaux disponibles pour cette date.
                    </p>
                  )}

                  <div className="reservation-time-grid">
                    {availableTimes.map((timeValue) => (
                      <button
                        key={timeValue}
                        type="button"
                        className={`reservation-time-chip${
                          formData.requestedTime === timeValue ? " active" : ""
                        }`}
                        onClick={() => handleTimeSelect(timeValue)}
                      >
                        {timeValue}
                      </button>
                    ))}
                  </div>

                  {formData.requestedDate && (
                    <p className="reservation-inline-note">
                      Date selectionnee:{" "}
                      {formatDateLabel(formData.requestedDate)}
                    </p>
                  )}
                </div>
              </div>

              <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="guestCount">Nombre de personnes *</label>
                  <input
                    id="guestCount"
                    className="form-control"
                    type="number"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    min="1"
                    max="20"
                    required
                  />
                </div>
              </div>

            </div>

            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              autoComplete="off"
              tabIndex={-1}
              style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            />

            <div className="row">
              <div className="col-sm-12">
                <div className="form-group">
                  <label htmlFor="message">Message (optionnel)</label>
                  <textarea
                    id="message"
                    className="form-control"
                    rows="5"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={1200}
                  />
                </div>
              </div>
            </div>

            {shouldRequireDeposit && (
              <div className="reservation-deposit-panel" aria-live="polite">
                  <h3>Caution carte bancaire</h3>
                <p
                  style={{ fontWeight: 700, color: "#8a1f2f", marginBottom: 8 }}
                >
                  <span role="img" aria-label="Attention">
                    ⚠️
                  </span>{" "}
                    {"Une empreinte de"}{" "}
                  {formatDepositAmount(
                    depositPreviewCents,
                    depositPolicy.currency,
                  )}{" "}
                    {"est demandée pour cette réservation."}
                  <br />
                  <span style={{ color: "#b94a48" }}>
                      Le montant ne sera débité qu'en cas d'annulation de dernière minute (moins de 24h avant) ou de non-présentation.
                  </span>
                </p>
                <ul
                  style={{
                    margin: "8px 0 12px 18px",
                    color: "#5c3f45",
                    fontSize: "0.98em",
                  }}
                >
                  <li>
                      Vous devez accepter la <b>politique de caution</b> et les{" "}
                    <b>conditions d'annulation</b> pour valider la réservation.
                  </li>
                  <li>
                      En cas d'annulation tardive ou d'absence, la caution pourra être débitée.
                  </li>
                  <li>Pour toute question, contactez-nous avant de valider.</li>
                </ul>
                {depositPolicy.testMode && (
                  <p className="reservation-inline-note reservation-inline-note-warning">
                      Mode test actif : utilisez une carte de test Stripe, aucun débit réel n'est effectué.
                  </p>
                )}
                <div className="reservation-card-field-wrap">
                  <label htmlFor="cbFirstName">Prénom sur la carte *</label>
                  <input
                    id="cbFirstName"
                    className="form-control"
                    type="text"
                    name="cbFirstName"
                    autoComplete="cc-given-name"
                    minLength={2}
                    maxLength={80}
                    required
                    placeholder="Prénom comme sur la carte"
                    value={formData.cbFirstName}
                    onChange={handleChange}
                  />
                  <label htmlFor="cbLastName">Nom sur la carte *</label>
                  <input
                    id="cbLastName"
                    className="form-control"
                    type="text"
                    name="cbLastName"
                    autoComplete="cc-family-name"
                    minLength={2}
                    maxLength={80}
                    required
                    placeholder="Nom comme sur la carte"
                    value={formData.cbLastName}
                    onChange={handleChange}
                  />
                  <label htmlFor="cbAddress">Adresse de facturation *</label>
                  <input
                    id="cbAddress"
                    className="form-control"
                    type="text"
                    name="cbAddress"
                    autoComplete="cc-address-line1"
                    minLength={5}
                    maxLength={120}
                    required
                    placeholder="Adresse complète de facturation"
                    value={formData.cbAddress}
                    onChange={handleChange}
                  />
                  <label htmlFor="cbPostal">Code postal *</label>
                  <input
                    id="cbPostal"
                    className="form-control"
                    type="text"
                    name="cbPostal"
                    autoComplete="cc-postal-code"
                    minLength={3}
                    maxLength={16}
                    required
                    placeholder="Code postal"
                    value={formData.cbPostal}
                    onChange={handleChange}
                  />
                  <label htmlFor="cbCity">Ville *</label>
                  <input
                    id="cbCity"
                    className="form-control"
                    type="text"
                    name="cbCity"
                    autoComplete="address-level2"
                    minLength={2}
                    maxLength={80}
                    required
                    placeholder="Ville de facturation"
                    value={formData.cbCity || ""}
                    onChange={handleChange}
                  />
                  <label htmlFor="reservation-card-element">
                    Carte bancaire *
                  </label>
                  <div
                    className="reservation-card-element"
                    id="reservation-card-element"
                  >
                    <CardElement
                      options={{
                        hidePostalCode: false,
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#2a0f16",
                            "::placeholder": {
                              color: "#7f6c70",
                            },
                          },
                          invalid: {
                            color: "#be3a3a",
                          },
                        },
                      }}
                    />
                  </div>
                  <ul
                    style={{
                      margin: "8px 0 0 18px",
                      color: "#7f6c70",
                      fontSize: "0.97em",
                    }}
                  >
                    <li>
                      Le nom et prénom doivent correspondre à ceux figurant sur
                      la carte.
                    </li>
                    <li>
                      Adresse complète et code postal requis pour la validation.
                    </li>
                    <li>
                      Seules les cartes Visa, Mastercard ou CB sont acceptées.
                    </li>
                    <li>
                      En cas de refus, vérifiez que votre carte autorise les
                      paiements internet et dispose de fonds suffisants.
                    </li>
                  </ul>
                </div>
                <label className="reservation-policy-checkbox">
                  <input
                    type="checkbox"
                    checked={acceptDepositPolicy}
                    onChange={(event) =>
                      setAcceptDepositPolicy(event.target.checked)
                    }
                  />
                  <span className="reservation-policy-text">
                    J'accepte la{" "}
                    <button
                      type="button"
                      className="reservation-policy-link"
                      onClick={() => setShowPolicyModal(true)}
                    >
                       politique de caution
                    </button>{" "}
                    et les{" "}
                    <button
                      type="button"
                      className="reservation-policy-link"
                      onClick={() => setShowCancelModal(true)}
                    >
                      conditions d'annulation de dernière minute
                    </button>
                    .
                  </span>
                </label>

                {showPolicyModal && (
                  <div
                    className="reservation-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="reservation-policy-modal-title"
                    onClick={() => setShowPolicyModal(false)}
                  >
                    <div
                      className="reservation-modal-card"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="reservation-modal-close"
                        aria-label="Fermer"
                        onClick={() => setShowPolicyModal(false)}
                      >
                        ×
                      </button>
                      <h2
                        id="reservation-policy-modal-title"
                        className="reservation-modal-title"
                      >
                          Politique de caution
                      </h2>
                      <ul className="reservation-modal-list">
                        <li>
                            Une empreinte bancaire est demandée pour garantir la réservation.
                        </li>
                        <li>
                            Le montant n'est débité qu'en cas d'annulation tardive (moins de 24h avant) ou de non-présentation.
                        </li>
                        <li>
                          En cas de problème, contactez-nous avant de valider.
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {showCancelModal && (
                  <div
                    className="reservation-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="reservation-cancel-modal-title"
                    onClick={() => setShowCancelModal(false)}
                  >
                    <div
                      className="reservation-modal-card"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="reservation-modal-close"
                        aria-label="Fermer"
                        onClick={() => setShowCancelModal(false)}
                      >
                        ×
                      </button>
                      <h2
                        id="reservation-cancel-modal-title"
                        className="reservation-modal-title"
                      >
                        Conditions d'annulation
                      </h2>
                      <ul className="reservation-modal-list">
                        <li>
                          Annulation gratuite jusqu'à 24h avant la réservation.
                        </li>
                        <li>
                            En cas d'annulation moins de 24h avant ou de non-présentation, la caution pourra être débitée.
                        </li>
                        <li>Pour toute question, contactez-nous.</li>
                      </ul>
                    </div>
                  </div>
                )}

                {shouldRequireDeposit &&
                  (stripeUnavailable || depositPolicyLoading) && (
                  <p className="reservation-inline-note reservation-inline-note-warning">
                    {stripeUnavailable
                      ? (unavailableMessage || "Configuration paiement manquante (REACT_APP_STRIPE_PUBLIC_KEY).")
                      : "Chargement du module de paiement..."}
                  </p>
                )}
              </div>
            )}

            <div className="row">
              <div className="col-sm-12">
                <div className="single-contact-btn">
                  <button
                    type="submit"
                    className="contact-btn"
                    disabled={
                      loading ||
                      settingsLoading ||
                      availabilityLoading ||
                      (shouldRequireDeposit &&
                        (depositPolicyLoading || stripeUnavailable))
                    }
                  >
                    {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                  </button>
                </div>
              </div>
            </div>

            </fieldset>

            {successMessage && (
              <div
                className="success-message reservation-feedback reservation-feedback-success"
                role="status"
              >
                <p className="message-send">{successMessage}</p>
              </div>
            )}

            {errorMessage && (
              <div
                className="success-message reservation-feedback reservation-feedback-error"
                role="alert"
              >
                <p className="message-send">{errorMessage}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function ReservationFormContentWithStripeContext(props) {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <ReservationFormContent {...props} stripe={stripe} elements={elements} />
  );
}

function Reservation({ forcedType = null, heading, manageDocumentTitle = true, stripeUnavailable: forceUnavailable = false, unavailableMessage = null, blocked = false }) {
  const formHeading =
    typeof heading === "string" && heading.trim().length > 0
      ? heading
      : "Reserver une table";

  const isUnavailable = forceUnavailable || !stripePromise;

  if (stripePromise) {
    return (
      <Elements stripe={stripePromise}>
        <ReservationFormContentWithStripeContext
          stripeUnavailable={isUnavailable}
          unavailableMessage={isUnavailable ? unavailableMessage : null}
          forcedType={forcedType}
          heading={formHeading}
          manageDocumentTitle={manageDocumentTitle}
          blocked={blocked}
        />
      </Elements>
    );
  }

  return (
    <ReservationFormContent
      stripeUnavailable
      unavailableMessage={unavailableMessage}
      forcedType={forcedType}
      heading={formHeading}
      manageDocumentTitle={manageDocumentTitle}
      blocked={blocked}
    />
  );
}

export default Reservation;
